import {
  Box,
  Button,
  Card,
  CardContent,
  Fab,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import UploadFileIcon from "@mui/icons-material/UploadFileOutlined";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { AppliedFilterChip } from "../components/AppliedFilterChips";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { accountOwnerName } from "../components/FinanceSelects";
import { TransactionList } from "../components/TransactionList";
import { ImportRecordsModal } from "../features/import/ImportRecordsModal";
import {
  RecordEditorPanel,
  RecordsFilterPanel,
  type RecordEditorState,
  type RecordFilters,
  type RecordMode,
  useRecordsQuery,
} from "../features/records";
import { useRecordsUrlAction } from "../features/records/useRecordsUrlAction";
import { financeApi } from "../lib/api";
import { useDevDateOverride } from "../lib/devDate";
import { categoryDisplayName, sortCategoriesHierarchically } from "../lib/format";
import { transactionTypeLabels } from "../lib/labels";
import { queryKeys } from "../lib/queryKeys";
import type { Group } from "../lib/types";

type RecordsPageProps = {
  group: Group;
  onBlockedAccess?: () => void;
};

type ModalState = { kind: "closed" } | RecordEditorState;

function initialFiltersFromUrl(): { filters: RecordFilters; search: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    filters: {
      type: (params.get("type") as RecordFilters["type"] | null) || "",
      categoryId: params.get("category") || "",
      uncategorized: params.get("uncategorized") === "1",
      userId: params.get("user") || "",
      accountId: params.get("account") || "",
      tagIds: params.getAll("tag"),
      dateFrom: params.get("from") || "",
      dateTo: params.get("to") || "",
    },
    search: params.get("q") || "",
  };
}

export function RecordsPage({ group, onBlockedAccess }: RecordsPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const asOfDate = useDevDateOverride();
  const { createAction, editAction, editReturnAction, importAction, clearCreateAction, clearEditAction, clearImportAction } = useRecordsUrlAction();
  const [initialUrlState] = useState(() => initialFiltersFromUrl());
  const [filtersOpen, setFiltersOpen] = useState(() => Object.entries(initialUrlState.filters).some(([, value]) => Array.isArray(value) ? value.length > 0 : Boolean(value)));
  const [mode, setMode] = useState<RecordMode>("active");
  const [filters, setFilters] = useState<RecordFilters>(() => initialUrlState.filters);
  const [search, setSearch] = useState(() => initialUrlState.search);
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [deleteAllError, setDeleteAllError] = useState("");
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (createAction) {
      setImportOpen(false);
      setModal({ kind: "create", initialType: createAction });
    } else if (editAction) {
      setImportOpen(false);
      void queryClient.fetchQuery({
        queryKey: queryKeys.record(group.id, editAction),
        queryFn: () => financeApi.transaction(group.id, editAction),
      }).then((transaction) => setModal({ kind: "edit", transaction })).catch(() => clearEditAction(editAction));
    } else if (importAction) {
      setModal({ kind: "closed" });
      setImportOpen(true);
    }
  }, [clearEditAction, createAction, editAction, group.id, importAction, queryClient]);

  useEffect(() => {
    const updateScrollTopVisibility = () => setShowScrollTop(window.scrollY > window.innerHeight * 1);
    updateScrollTopVisibility();
    window.addEventListener("scroll", updateScrollTopVisibility, { passive: true });
    window.addEventListener("resize", updateScrollTopVisibility);
    return () => {
      window.removeEventListener("scroll", updateScrollTopVisibility);
      window.removeEventListener("resize", updateScrollTopVisibility);
    };
  }, []);

  const closeRecordModal = () => {
    setModal({ kind: "closed" });
    if (createAction) clearCreateAction(createAction);
    if (editAction && editReturnAction === "statistics") navigate(-1);
    else if (editAction) clearEditAction(editAction);
  };

  const closeImport = () => {
    setImportOpen(false);
    if (importAction) clearImportAction();
  };

  const {
    accounts,
    categories,
    tags,
    currencies,
    members,
    visibleRecords,
    hasMoreRecords,
    loadMoreRef,
    deletedRecordsPreview,
    isLoading,
  } = useRecordsQuery({ group, mode, filters, search, asOfDate, onBlockedAccess });

  const emptyDeletedMutation = useMutation({
    mutationFn: () => financeApi.emptyDeletedTransactions(group.id),
    onSuccess: async () => {
      setConfirmDeleteAll(false);
      setDeleteAllError("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
        queryClient.invalidateQueries({ queryKey: queryKeys.records }),
        queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
      ]);
    },
    onError: (error) => setDeleteAllError(error instanceof Error ? error.message : "Request failed"),
  });

  const filterCategories = sortCategoriesHierarchically(
    categories.filter((category) => !category.is_archived).filter((category) => !filters.type || filters.type === "linked" || category.type === filters.type),
    categories,
  );
  const recordsById = useMemo(() => new Map(visibleRecords.map((record) => [record.id, record])), [visibleRecords]);

  // Open a record by id — used for a linked partner leg that isn't in the loaded/filtered
  // list (only its embedded summary is on the page), so we fetch the full record on click.
  const openRecordById = async (id: string) => {
    const cached = recordsById.get(id);
    if (cached) {
      setModal({ kind: "edit", transaction: cached });
      return;
    }
    try {
      const record = await queryClient.fetchQuery({
        queryKey: queryKeys.record(group.id, id),
        queryFn: () => financeApi.transaction(group.id, id),
      });
      setModal({ kind: "edit", transaction: record });
    } catch {
      // ignore — the partner may have been deleted since the page loaded
    }
  };
  const hasDeletedRecords = Boolean(deletedRecordsPreview?.items.length || deletedRecordsPreview?.has_more);
  const selectedFilterChips = [
    filters.type ? { key: "type", label: filters.type === "linked" ? "Linked transfer" : transactionTypeLabels[filters.type] } : null,
    filters.categoryId
      ? { key: "categoryId", label: categories.find((category) => category.id === filters.categoryId) ? categoryDisplayName(categories.find((category) => category.id === filters.categoryId)!, categories) : "Category" }
      : null,
    filters.uncategorized ? { key: "uncategorized", label: "Uncategorized" } : null,
    filters.userId ? { key: "userId", label: members.find((member) => member.user_id === filters.userId)?.display_name || "Person" } : null,
    filters.accountId
      ? {
          key: "accountId",
          label: (() => {
            const account = accounts.find((item) => item.id === filters.accountId);
            if (!account) return "Account";
            const ownerName = accountOwnerName(account, members);
            return ownerName ? `${account.name} · ${ownerName}` : account.name;
          })(),
        }
      : null,
    filters.dateFrom ? { key: "dateFrom", label: `From ${filters.dateFrom}` } : null,
    filters.dateTo ? { key: "dateTo", label: `To ${filters.dateTo}` } : null,
  ].filter(Boolean) as AppliedFilterChip<keyof RecordFilters>[];
  const clearFilter = (key: keyof RecordFilters) => {
    setFilters((current) => ({ ...current, [key]: key === "uncategorized" ? false : key === "tagIds" ? [] : "" }));
  };

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>{mode === "deleted" ? "Deleted records" : "Records"}</h1>
          <p>Income and expenses grouped by month and day.</p>
        </Box>
        {mode === "active" ? (
          <Box className="records-topbar-actions">
            <Button
              className="records-desktop-import-action"
              variant="outlined"
              type="button"
              startIcon={<UploadFileIcon fontSize="small" />}
              onClick={() => setImportOpen(true)}
            >
              Import
            </Button>
            <Button
              className="records-desktop-add-action"
              variant="contained"
              type="button"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setModal({ kind: "create" })}
            >
              Add record
            </Button>
            <IconButton
              className="records-mobile-import-action"
              type="button"
              aria-label="Import"
              title="Import records"
              onClick={() => setImportOpen(true)}
            >
              <UploadFileIcon />
            </IconButton>
            <IconButton
              className="records-mobile-add-action"
              type="button"
              aria-label="Add record"
              title="Add record"
              onClick={() => setModal({ kind: "create" })}
            >
              <AddIcon />
            </IconButton>
          </Box>
        ) : null}
      </header>

      <section className="content">
        <Card className="records-panel">
          <CardContent className="records-panel-content">
            {mode === "active" ? (
              hasDeletedRecords ? (
                <Box className="records-toolbar">
                  <Box className="records-utility-actions">
                    <Button variant="outlined" type="button" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => setMode("deleted")}>
                      Deleted records
                    </Button>
                  </Box>
                </Box>
              ) : null
            ) : (
              <Box className="records-toolbar">
                <>
                  <Box className="records-primary-actions"></Box>
                  <Box className="records-utility-actions">
                    {hasDeletedRecords ? (
                      <Button variant="outlined" color="error" type="button" onClick={() => setConfirmDeleteAll(true)}>
                        Delete all
                      </Button>
                    ) : null}
                    <Button variant="outlined" type="button" onClick={() => setMode("active")}>
                      Back to records
                    </Button>
                  </Box>
                </>
              </Box>
            )}

            {mode === "active" ? (
              <RecordsFilterPanel
                filters={filters}
                setFilters={setFilters}
                filtersOpen={filtersOpen}
                setFiltersOpen={setFiltersOpen}
                search={search}
                setSearch={setSearch}
                categories={categories}
                filterCategories={filterCategories}
                accounts={accounts}
                members={members}
                tags={tags}
                chips={selectedFilterChips}
                onClearFilter={clearFilter}
              />
            ) : null}

            {isLoading ? <p className="status">Loading records...</p> : null}
            {!isLoading && !visibleRecords.length ? <p className="status">{mode === "deleted" ? "No deleted records." : "No records found"}</p> : null}
            <TransactionList
              transactions={visibleRecords}
              accounts={accounts}
              categories={categories}
              members={members}
              baseCurrency={group.base_currency}
              onOpen={(transaction) => setModal({ kind: "edit", transaction })}
              onOpenId={openRecordById}
              hasMore={hasMoreRecords}
              loadMoreRef={loadMoreRef}
            />
          </CardContent>
        </Card>
      </section>

      {showScrollTop ? (
        <Fab
          className="records-scroll-top-fab"
          color="primary"
          size="medium"
          type="button"
          aria-label="Back to top"
          title="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      ) : null}

      {modal.kind !== "closed" ? (
        <RecordEditorPanel
          key={modal.kind === "create" ? `create:${modal.initialType || "expense"}` : `edit:${modal.transaction.id}`}
          state={modal}
          group={group}
          accounts={accounts}
          members={members}
          categories={categories}
          tags={tags}
          currencies={currencies}
          onClose={closeRecordModal}
        />
      ) : null}

      {importOpen ? (
        <ImportRecordsModal group={group} accounts={accounts} members={members} categories={categories} tags={tags} currencies={currencies} onClose={closeImport} />
      ) : null}

      {confirmDeleteAll ? (
        <ConfirmDialog
          titleId="deleteAllRecordsTitle"
          title="Delete all records?"
          description="This permanently deletes every record currently in Deleted records and cannot be undone."
          confirmLabel="Delete all"
          closeAriaLabel="Close delete all records dialog"
          pending={emptyDeletedMutation.isPending}
          error={deleteAllError}
          onConfirm={() => emptyDeletedMutation.mutate()}
          onClose={() => setConfirmDeleteAll(false)}
        />
      ) : null}
    </>
  );
}
